import { ExtendedObservation } from '@seen/core/models/observation'
import type {
  Condition,
  Operand,
} from '@seen/core/observation-logic/conditionSchema'
import {
  answeredSet,
  type ConditionValue,
  conditionValueKey,
  frozenPanelTotal,
  groupValueKey,
  isConditionalWrapper,
  isSectionLike,
  latestValueKey,
  OBSERVATION_NODE,
  OBSERVATION_PANEL_NODE,
  readCondition,
} from '@seen/core/observation-logic/documentUnits'
import {
  describeCondition,
  type Tri,
} from '@seen/core/observation-logic/evaluate'
import {
  type InstrumentAnswers,
  instrumentAnswerKey,
  instrumentSummary,
} from '@seen/core/observation-logic/instruments/records'
import {
  draftValue,
  hasDraft,
  panelAnswers as heldPanelAnswers,
} from '@seen/core/observation-logic/noteCapture'
import {
  evaluateShowWhen,
  type ShowWhenNode,
  type ShowWhenResult,
  type ShowWhenSection,
} from '@seen/core/observation-logic/showWhen'
import type { ObservationValue } from '@seen/core/observation-logic/value'
import {
  conditionVariables,
  type ResourceBindingContext,
  resourceBindingConditionValue,
} from '@seen/core/resourceBindingRegistry'
import { panelTemplate } from '@seen/core/terminology/instruments/loincPanels'
import type { Node as PMNode } from '@tiptap/pm/model'
import { panelSources } from '#src/components/rich-text-editor/conditional-display/conditionBuilderModel.ts'

// The editor re-exports these for code that reads the walker's value map.
export {
  type ConditionValue,
  conditionValueKey,
  groupValueKey,
  OBSERVATION_NODE,
  OBSERVATION_PANEL_NODE,
} from '@seen/core/observation-logic/documentUnits'

// Answers an instrument panel holds before they are written, by panel id
// then `instrumentAnswerKey`.
export type PanelAnswers = ReadonlyMap<string, InstrumentAnswers>

export interface ConditionalUnits {
  nodes: ShowWhenNode[]
  sections: ShowWhenSection[]
  // Labels for chips: an observation by its id, name and code.
  labels: Map<string, string>
  // Answer displays by an observation's name or code, then answer code.
  answerLabels?: Map<string, Map<string, string>>
}

// The observations and sections of a document, in order, as the evaluator
// reads them.
export function collectConditionalUnits(
  doc: PMNode,
  values: ReadonlyMap<string, ConditionValue>,
  panelAnswers: PanelAnswers = new Map()
): ConditionalUnits {
  const nodes: ShowWhenNode[] = []
  const sections: ShowWhenSection[] = []
  const labels = new Map<string, string>()
  const answerLabels = new Map<string, Map<string, string>>()
  const addAnswers = (
    keys: (string | null | undefined)[],
    options: readonly { code?: string; display?: string }[] | null | undefined
  ): void => {
    const displays = new Map(
      (options ?? []).flatMap((option) =>
        option.code && option.display ? [[option.code, option.display]] : []
      )
    )
    if (!displays.size) {
      return
    }
    for (const key of keys) {
      if (key && !answerLabels.has(key)) {
        answerLabels.set(key, displays)
      }
    }
  }
  doc.descendants((node, pos) => {
    if (isSectionLike(node.type.name) && node.attrs.id) {
      const parent = enclosingSection(doc, pos)
      const showWhen = readCondition(node.attrs.showWhen)
      const section: ShowWhenSection = {
        id: node.attrs.id,
        index: nodes.length,
      }
      if (parent) {
        section.parentId = parent
      }
      if (showWhen) {
        section.showWhen = showWhen
      }
      sections.push(section)
      if (node.type.name === OBSERVATION_PANEL_NODE) {
        for (const unit of panelUnits(node, values, panelAnswers)) {
          nodes.push(unit.node)
          labels.set(unit.node.code, unit.label)
          addAnswers([unit.node.code], unit.answers)
        }
      }
      return true
    }
    if (node.type.name === OBSERVATION_NODE && node.attrs.id) {
      const sectionId = enclosingSection(doc, pos)
      const showWhen = readCondition(node.attrs.showWhen)
      const key = conditionValueKey(node.attrs)
      const label = ExtendedObservation.getNickname(
        node.attrs.code,
        node.attrs.codingDisplay || node.attrs.display
      )
      if (node.attrs.name) {
        labels.set(node.attrs.name, label)
      }
      if (node.attrs.code) {
        labels.set(node.attrs.code, label)
      }
      addAnswers([node.attrs.name, node.attrs.code], node.attrs.answerOptions)
      const observation: ShowWhenNode = {
        id: node.attrs.id,
        code: node.attrs.code ?? '',
        // A held draft is the node's answer until the note is signed.
        ...(hasDraft(node.attrs)
          ? { value: draftValue(node.attrs) }
          : (answeredSet(node.attrs) ??
            nodeValue(key ? values.get(key) : null))),
      }
      if (node.attrs.system) {
        observation.system = node.attrs.system
      }
      if (node.attrs.name) {
        observation.name = node.attrs.name
      }
      if (sectionId) {
        observation.sectionId = sectionId
      }
      if (showWhen) {
        observation.showWhen = showWhen
      }
      nodes.push(observation)
      return false
    }
    return true
  })
  return { nodes, sections, labels, answerLabels }
}

// How chips, hints and the sheet word a condition: operands by their
// labels, coded answers by their displays.
export function conditionDescriber(
  units: Pick<ConditionalUnits, 'labels' | 'answerLabels'>
): (condition: Condition) => string {
  const label = operandLabeller(units.labels)
  return (condition) =>
    describeCondition(condition, label, (operand, literal) =>
      operand.source === 'observation' && literal.kind === 'coding'
        ? units.answerLabels?.get(operand.code)?.get(literal.code)
        : undefined
    )
}

// Mirrors core's private `nodeValue` in `documentUnits.ts`.
function nodeValue(
  value: ConditionValue | undefined
): Pick<ShowWhenNode, 'value' | 'pending'> {
  return value === 'missing'
    ? { value: null, pending: true }
    : { value: value ?? null }
}

interface PanelUnit {
  node: ShowWhenNode
  label: string
  answers?: readonly { code?: string; display?: string }[]
}

// An instrument panel's items and total as observations conditions can
// read, inside the panel (hidden with it). Answers it holds come first;
// else its written records.
function panelUnits(
  panel: PMNode,
  values: ReadonlyMap<string, ConditionValue>,
  panelAnswers: PanelAnswers
): PanelUnit[] {
  const template = panelTemplate(panel.attrs)
  const panelId = panel.attrs.id as string
  if (!template) {
    return []
  }
  // Answers the panel holds in the note count before any record exists.
  const held =
    panelAnswers.get(panelId) ?? heldPanelAnswers(panel.attrs) ?? undefined
  const groupId = panel.attrs.groupId as string | null
  const recorded = (coding: { system?: string; code?: string }) =>
    nodeValue(
      groupId
        ? values.has(groupValueKey(groupId))
          ? 'missing'
          : values.get(groupValueKey(groupId, coding))
        : null
    )
  const total = () =>
    held ? totalValue(instrumentSummary(template, held, {}).total) : null
  // Frozen to the latest value, the panel shows (and so conditions read)
  // the participant's latest total, not its retained capture's.
  const frozenTotal = frozenPanelTotal(panel.attrs)
  const totalUnit = (coding: {
    system?: string
    code?: string
  }): Pick<ShowWhenNode, 'value' | 'pending'> => {
    if (frozenTotal) {
      return nodeValue(
        values.get(latestValueKey(frozenTotal.system, frozenTotal.code))
      )
    }
    return held ? { value: total() } : recorded(coding)
  }
  return panelSources(template).map((source) => {
    const coding = { system: source.system ?? undefined, code: source.code }
    const key = instrumentAnswerKey(coding)
    const node: ShowWhenNode = {
      id: `${panelId}/${key}`,
      code: source.code,
      sectionId: panelId,
      ...(source.isTotal
        ? totalUnit(coding)
        : held
          ? { value: held[key] ?? null }
          : recorded(coding)),
    }
    if (source.system) {
      node.system = source.system
    }
    const unit: PanelUnit = { node, label: source.label }
    if (!source.isTotal) {
      unit.answers = source.answerOptions
    }
    return unit
  })
}

function totalValue(total: number | null): ObservationValue | null {
  return total === null ? null : { kind: 'number', value: total }
}

function enclosingSection(doc: PMNode, pos: number): string | undefined {
  const $pos = doc.resolve(pos)
  for (let depth = $pos.depth; depth > 0; depth--) {
    const ancestor = $pos.node(depth)
    if (isConditionalWrapper(ancestor.type.name) && ancestor.attrs.id) {
      return ancestor.attrs.id
    }
  }
  return undefined
}

export function evaluateDocument(
  units: ConditionalUnits,
  contexts: ResourceBindingContext
): ShowWhenResult {
  return evaluateShowWhen(units.nodes, units.sections, (id) =>
    resourceBindingConditionValue(id, contexts)
  )
}

const VARIABLE_LABELS = new Map(
  conditionVariables().flatMap((variable) => [
    [variable.id, variable.label],
    [variable.alias, variable.label],
  ])
)

// How a chip names an operand: a context variable by its label, an
// observation by the label of the node it reads.
function operandLabeller(
  labels: ReadonlyMap<string, string>
): (operand: Operand) => string | undefined {
  return (operand) =>
    operand.source === 'variable'
      ? VARIABLE_LABELS.get(operand.id)
      : labels.get(operand.code)
}

export function triState(
  value: Tri | undefined
): 'shown' | 'hidden' | 'unknown' {
  if (value === 'unknown') {
    return 'unknown'
  }
  return value === false ? 'hidden' : 'shown'
}

// The ids of the observation nodes the conditions hide right now, from the
// display plugin's last result (read through `CONDITIONAL_DISPLAY_KEY`).
export function hiddenNodeIds(result: ShowWhenResult | undefined): string[] {
  if (!result) {
    return []
  }
  return [...result.nodes.entries()]
    .flatMap(([id, shown]) => (shown === false ? [id] : []))
    .sort()
}
